<script setup lang="ts">
import { ref, computed } from "vue";
import { useAuthStore } from "@/stores/authStore";
import { storeToRefs } from "pinia";
import { signDocuments } from "@/services/legal-documents.services";
import { useRouter } from "vue-router";
import { XIcon } from "@heroicons/vue/outline";

const authStore = useAuthStore();
const { user, documentHashes } = storeToRefs(authStore);
const router = useRouter();

const acceptedPolicy = ref(false);
const loading = ref(false);

interface DialogContent {
  title: string;
  description: string;
  showTyC: boolean;
  showPP: boolean;
}

const dialogContent = computed<DialogContent>(() => {
  const defaultContent: DialogContent = {
    title: "Términos y condiciones y Política de privacidad",
    description:
      "Para continuar utilizando la plataforma, es necesario que aceptes nuestros Términos y condiciones y Política de privacidad.",
    showTyC: true,
    showPP: true,
  };

  if (!user.value) return defaultContent;

  if (user.value.already_signed === false) {
    return defaultContent;
  }

  if (user.value.already_signed === true) {
    const hasTyC = user.value.has_signed_tyc;
    const hasPP = user.value.has_signed_pp;

    if (hasTyC === false && hasPP === true) {
      return {
        title: "Actualización de Términos y condiciones",
        description:
          "Para continuar utilizando la plataforma, es necesario que aceptes nuestros Términos y condiciones, que fueron actualizados recientemente.",
        showTyC: true,
        showPP: false,
      };
    }

    if (hasPP === false && hasTyC === true) {
      return {
        title: "Actualización de Política de privacidad",
        description:
          "Para continuar utilizando la plataforma, es necesario que aceptes nuestra Política de privacidad, que fue actualizada recientemente.",
        showTyC: false,
        showPP: true,
      };
    }

    if (user.value.all_signed === false) {
      return {
        title:
          "Actualización de Términos y condiciones y Política de privacidad",
        description:
          "Para continuar utilizando la plataforma, es necesario que aceptes nuestros Términos y condiciones y Política de privacidad, que fueron actualizados recientemente.",
        showTyC: true,
        showPP: true,
      };
    }
  }

  return defaultContent;
});

const handleAccept = async () => {
  if (!acceptedPolicy.value || !user.value?.user_id) return;

  loading.value = true;
  try {
    await signDocuments({
      email: user.value.email || "",
      document_hashes: documentHashes.value,
      origin: "GO",
      pedido_id: user.value.user_id,
    });

    authStore.allDocumentsSigned = true;
    authStore.showComplianceDialog = false;
    router.push({ name: "Inicio" });
  } catch (error) {
    console.error("Error al aceptar la política:", error);
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="dialog-overlay">
    <div class="dialog-container">
      <div class="dialog-card">
        <!-- Header -->
        <div class="dialog-header">
          <h2 class="dialog-title">{{ dialogContent.title }}</h2>
        </div>

        <!-- Content -->
        <div class="dialog-content">
          <p class="dialog-text">
            {{ dialogContent.description }}
          </p>

          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="acceptedPolicy"
              class="checkbox-input"
            />
            <span class="checkbox-text">
              Acepto
              <template v-if="dialogContent.showTyC && dialogContent.showPP">
                los
                <a
                  href="https://vaitty.com/terminos"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-link"
                  >Términos y condiciones</a
                >
                y
                <a
                  href="https://vaitty.com/privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-link"
                  >Política de privacidad</a
                >
              </template>
              <template v-else-if="dialogContent.showTyC">
                los
                <a
                  href="https://vaitty.com/terminos"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-link"
                  >Términos y condiciones</a
                >
              </template>
              <template v-else-if="dialogContent.showPP">
                la
                <a
                  href="https://vaitty.com/privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-link"
                  >Política de privacidad</a
                >
              </template>
            </span>
          </label>
        </div>

        <!-- Footer -->
        <div class="dialog-footer">
          <button
            :disabled="!acceptedPolicy || loading"
            @click="handleAccept"
            class="accept-button"
            :class="{ 'button-disabled': !acceptedPolicy }"
          >
            <span v-if="loading">Cargando...</span>
            <span v-else>ACEPTAR Y CONTINUAR</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.dialog-container {
  width: 100%;
  max-width: 573px;
  box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.2),
    0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12);
}

.dialog-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 24px;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.dialog-title {
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  font-style: normal;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
  flex: 1;
  word-wrap: break-word;
}

.dialog-text {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  font-style: normal;
  letter-spacing: 0.08px;
  font-feature-settings: "liga" off;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 32px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  margin-bottom: 32px;
}

.checkbox-input {
  margin-top: 2px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  accent-color: #5b7fff;
  outline: none;
}

.checkbox-input:focus {
  outline: none;
  box-shadow: none;
}

.checkbox-text {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
  flex: 1;
}

.text-link {
  color: #5b7fff;
  text-decoration: none;
  font-weight: 500;
}

.text-link:hover {
  text-decoration: underline;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}

.accept-button {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background-color: #5b7fff;
  color: white;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.accept-button:hover:not(:disabled) {
  background-color: #4a6de6;
}

.accept-button:disabled,
.button-disabled {
  background-color: #e0e0e0 !important;
  color: rgba(0, 0, 0, 0.38) !important;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .dialog-container {
    max-width: 338px !important;
  }

  .dialog-title {
    font-size: 16px;
  }

  .dialog-text {
    font-size: 14px;
  }

  .dialog-card {
    padding: 20px;
  }

  .accept-button {
    width: 100%;
  }
}
</style>
